import { LUCK_PRINT_WIDTH_PX } from "@/modules/chat-tools/domain/luck-printer/protocol";
import { expectedRasterByteLength } from "@/modules/chat-tools/domain/luck-printer/raster";

/** Threshold grayscale → 1-bit raster (MSB-first). Dark pixel = 1. */
export function imageDataToLuckRaster(
  imageData: ImageData,
  threshold = 128,
): Uint8Array {
  const { width, height, data } = imageData;
  const rowBytes = Math.ceil(width / 8);
  const output = new Uint8Array(expectedRasterByteLength(width, height));

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const r = data[i] ?? 255;
      const g = data[i + 1] ?? 255;
      const b = data[i + 2] ?? 255;
      const a = data[i + 3] ?? 255;
      const avg = a < 128 ? 255 : (r + g + b) / 3;
      if (avg >= threshold) continue;

      const byteIndex = y * rowBytes + (x >> 3);
      const bit = 7 - (x & 7);
      output[byteIndex] = (output[byteIndex] ?? 0) | (1 << bit);
    }
  }

  return output;
}

export function normalizeLabelWidth(imageData: ImageData): ImageData {
  if (imageData.width === LUCK_PRINT_WIDTH_PX) return imageData;

  const scale = LUCK_PRINT_WIDTH_PX / imageData.width;
  const targetHeight = Math.max(1, Math.ceil(imageData.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = LUCK_PRINT_WIDTH_PX;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, LUCK_PRINT_WIDTH_PX, targetHeight);

  const source = document.createElement("canvas");
  source.width = imageData.width;
  source.height = imageData.height;
  const sourceCtx = source.getContext("2d");
  if (!sourceCtx) throw new Error("Canvas not supported");
  sourceCtx.putImageData(imageData, 0, 0);
  ctx.drawImage(source, 0, 0, LUCK_PRINT_WIDTH_PX, targetHeight);

  return ctx.getImageData(0, 0, LUCK_PRINT_WIDTH_PX, targetHeight);
}
